import type {
  Query,
  accountAssignmentType_bool_exp,
  accountAssignmentType_enum,
} from '@vrplatform/graphql';
import { formatAssignmentType } from '@vrplatform/ui-common';
import { useQuery } from '../graphql';
import { useEnabledFeatures } from '../hooks/useEnabledFeatures';
import { useTeam } from '../hooks/team';

export function useMissingAccountAssignmentsQuery() {
  const [{ id: teamId }] = useTeam();
  const { OPEX } = useEnabledFeatures();

  return useQuery(
    (q, args) => {
      return getMissingAccountAssignments(q, args);
    },
    {
      queryKey: 'accounts',
      variables: {
        teamId,
        OPEX,
      },
    }
  );
}

export function getMissingAccountAssignments(
  q: Query,
  args: {
    teamId: string;
    whereAccountAssigments?: accountAssignmentType_bool_exp;
    OPEX: boolean;
  }
) {
  const assignments = q
    .accountAssignmentTypes({
      where: args.whereAccountAssigments
        ? args.whereAccountAssigments
        : args.OPEX
          ? undefined
          : {
              name: { _neq: 'dueToFromTrust' },
            },
    })
    .map((x) => ({
      id: x.name as accountAssignmentType_enum,
      title: formatAssignmentType(x.name as accountAssignmentType_enum),
    }));

  const existingAssignments = q
    .accountAssignments({
      where: {
        tenantId: { _eq: args.teamId },
      },
      distinct_on: ['type'],
    })
    .map((account) => account.type!);

  const bankAccount = q
    .accounts({
      where: {
        type: { _eq: 'bank' },
        tenantId: { _eq: args.teamId },
        status: { _eq: 'active' },
      },
      limit: 1,
    })
    .map((x) => x.id)[0];

  const accountAssignments = !bankAccount
    ? assignments.filter((x) => x.id !== 'payout_bankAccount')
    : assignments;

  return accountAssignments.filter((x) => !existingAssignments.includes(x.id));
}
